<template>
  <div class="w-full h-full">
    <div
      class="relative w-full h-full cursor-pointer overflow-hidden"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @click="previewVideo"
    >
      <!-- 视频加载失败状态 -->
      <div
        v-if="loadError || coverLoadError || !cover"
        class="absolute inset-0 w-full h-full flex flex-col items-center justify-center bg-fill-2 z-up"
      >
        <Icon
          icon="sky:ic-image-empty-fill"
          class="text-text-4 size-14"
        />
        <span class="mt-2 text-xs text-text-3">{{ $t('common.resource_load_failed') }}</span>
      </div>
      <template v-else>
        <!-- 视频层 - 使用 z-index=-1 放在图片下方，hover 时显示 -->
        <video
          v-if="video && shouldLoadVideo"
          :key="videoRetryKey"
          ref="videoRef"
          :src="video"
          class="absolute inset-0 w-full h-full object-contain transition-opacity duration-300 z-[-1]"
          :class="{ 'opacity-0': !isHovered, 'opacity-100': isHovered, 'z-up': isHovered }"
          preload="none"
          loop
          muted
          playsinline
          autoplay
          @error="handleVideoError"
        />
        <!-- 图片层 - 默认显示，在视频上方 -->
        <el-image
          :key="coverRetryKey"
          class="absolute top-0 left-0 w-full h-full"
          style="filter: blur(5px);"
          :src="cachedCoverUrl"
          fit="cover"
          @error="handleCoverError"
        >
          <template #error>
            <div class="w-full h-full flex items-center justify-center bg-fill-2">
              <Icon
                icon="sky:ic-image-empty-fill"
                class="size-14 text-text-4"
              />
            </div>
          </template>
        </el-image>
        <img
          :key="coverRetryKey"
          :src="cachedCoverUrl"
          class="absolute inset-0 w-full h-full object-contain transition-opacity duration-300 z-0"
          alt=""
          @error="handleCoverError"
        >
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onUnmounted } from 'vue'
import { usePreviewVideo, type VideoPreviewItem } from '@/hooks/use-preview-video'
import { useCachedResource } from '@/hooks/use-cached-resource'

const RETRY_DELAY_MS = 5000
const MAX_RETRY_COUNT = 5

type Props = {
  cover: string
  video?: string
  previewVideoList?: VideoPreviewItem[]
  initialIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  video: '',
  previewVideoList: () => [],
  initialIndex: 0,
})

const isHovered = ref(false)
const shouldLoadVideo = ref(false)
const videoRef = ref<HTMLVideoElement | null>(null)
const loadError = ref(false)
const coverLoadError = ref(false)
const videoRetryKey = ref(0)
const videoRetryCount = ref(0)
let videoRetryTimer: ReturnType<typeof setTimeout> | null = null
const coverRetryKey = ref(0)
const coverRetryCount = ref(0)
let coverRetryTimer: ReturnType<typeof setTimeout> | null = null

const { showPreview } = usePreviewVideo()

// 使用缓存的封面图片（含 error、reload，用于 404 重试）
const coverUrlRef = computed(() => props.cover)
const { cachedUrl: cachedCoverUrl, error: coverCacheError, reload: reloadCoverCache } = useCachedResource(coverUrlRef)

const hasValidCoverAddress = computed(() => props.cover.trim() !== '')
const hasValidVideoAddress = computed(() => props.video.trim() !== '')

const scheduleCoverRetry = () => {
  if (!hasValidCoverAddress.value || coverRetryCount.value >= MAX_RETRY_COUNT) return
  if (coverRetryTimer) clearTimeout(coverRetryTimer)
  coverRetryTimer = setTimeout(() => {
    coverLoadError.value = false
    coverRetryCount.value += 1
    coverRetryKey.value += 1
    reloadCoverCache()
    coverRetryTimer = null
  }, RETRY_DELAY_MS)
}

// 处理封面加载失败：仅在有地址且请求失败（如 404）时重试
const handleCoverError = () => {
  if (coverRetryCount.value >= MAX_RETRY_COUNT) {
    coverLoadError.value = true
    return
  }
  coverLoadError.value = true
  scheduleCoverRetry()
}

// useCachedResource 内封面请求失败（如 404）时也触发 5 秒后重试
watch(coverCacheError, err => {
  if (err && hasValidCoverAddress.value) {
    scheduleCoverRetry()
  }
})

// 处理视频加载失败：仅在有地址且请求失败（如 404）时重试，5 秒后重试，最多 5 次
const handleVideoError = () => {
  loadError.value = true
  if (!hasValidVideoAddress.value || videoRetryCount.value >= MAX_RETRY_COUNT) return
  if (videoRetryTimer) clearTimeout(videoRetryTimer)
  videoRetryTimer = setTimeout(() => {
    loadError.value = false
    videoRetryCount.value += 1
    videoRetryKey.value += 1
    videoRetryTimer = null
  }, RETRY_DELAY_MS)
}

const handleMouseEnter = () => {
  isHovered.value = true
  if (props.video && !shouldLoadVideo.value) {
    shouldLoadVideo.value = true
  }
  if (videoRef.value && props.video) {
    videoRef.value.play().catch(() => {
      // 忽略播放错误
    })
  }
}

const handleMouseLeave = () => {
  isHovered.value = false
  if (videoRef.value) {
    videoRef.value.pause()
    videoRef.value.currentTime = 0
  }
}

const previewVideo = () => {
  if (props.previewVideoList.length === 0) return
  const videoList: VideoPreviewItem[] = props.previewVideoList

  const validIndex = Math.max(0, Math.min(props.initialIndex ?? 0, videoList.length - 1))

  showPreview({
    videoList,
    currentIndex: validIndex,
    onClose: () => {
      // 关闭回调
    },
    onNext: () => {
      // 下一个回调
    },
    onPrev: () => {
      // 上一个回调
    },
    onDownload: () => {
      // 下载回调
    },
  })
}

// 封面变化时重置封面加载错误状态与重试状态
watch(() => props.cover, () => {
  coverLoadError.value = false
  coverRetryCount.value = 0
  if (coverRetryTimer) {
    clearTimeout(coverRetryTimer)
    coverRetryTimer = null
  }
})

// 视频地址变化时重置重试状态
watch(() => props.video, () => {
  videoRetryCount.value = 0
  loadError.value = false
  if (videoRetryTimer) {
    clearTimeout(videoRetryTimer)
    videoRetryTimer = null
  }
})

onUnmounted(() => {
  if (videoRetryTimer) clearTimeout(videoRetryTimer)
  if (coverRetryTimer) clearTimeout(coverRetryTimer)
})

// 监听 video 元素加载，确保视频准备就绪
watch([() => shouldLoadVideo.value, () => videoRef.value], ([loaded, videoEl]) => {
  if (loaded && videoEl && props.video) {
    videoEl.load()
  }
})
</script>

<style scoped>
:deep(.el-dialog__headerbtn .el-dialog__close) {
  color: #fff;
  font-size: 26px;
}
:deep(.el-dialog__headerbtn .el-dialog__close:hover) {
  color: var(--el-color-primary);
}
</style>

